import React, { useEffect, useState, forwardRef, useRef } from 'react';
import { useSelector } from 'react-redux';
import DatePicker from 'react-datepicker';
import { useForm, FormProvider } from 'react-hook-form';
import { useAppDispatch, RootState } from '@/store';
import { getIncomeGraph, getCountGraph } from '@/store/home/action';
import { getVenueDetail } from '@/store/venue-detail/action';
import { getUserVenues } from '@/store/user-info/action';
import { getCalendar } from '@/store/calendar/action';
import { getBookingList } from '@/store/bookings/action';
import { getReportCsvUrl } from '@/store/report-csv/action';
import { getCountCsvUrl } from '@/store/count-csv/action';
import EmptyLayout from '@/components/common/CustomLayout/EmptyLayout';
import Button from '@/components/common/Button';
import Calendar from '@/components/common/Calendar';
import Table from '@/components/common/Table';
import { HEADERS, constructTableData, META, SORT } from '@/pages/Home/Table';
import { ReactComponent as RiArrowLeft } from '@/assets/icons/arrow-left.svg';
import { ReactComponent as RiArrowRight } from '@/assets/icons/arrow-right.svg';
import { ReactComponent as RiArrowUp } from '@/assets/icons/arrow-up.svg';
import { ReactComponent as RiArrowDown } from '@/assets/icons/arrow-down.svg';
import { ReactComponent as RiDownloadLine } from '@/assets/icons/ri-download-line.svg';
import { currencyFormat } from '@/utils';
import ReportCard from './ReportCard';
import UserVenueSelect from "@/components/common/UserVenueSelect";
import { openingHours } from '@/utils/validation';

const OPENING_HOURS = openingHours;
const options = {
	responsive: true,
	plugins: {
		legend: {
			display: false,
			position: 'top' as const,
		},
		title: {
			display: false,
		},
	},
	scales: {
		x: {
			ticks: {
				display: false,
				beginAtZero: true,
			},
			grid: {
				drawBorder: false,
				display: false,
			},
		},
		y: {
			ticks: {
				display: false,
				beginAtZero: true,
			},
			grid: {
				drawBorder: false,
				display: false,
			},
		},
	},
};
const ShowCalendar = forwardRef<any>(
	(
		{
			value,
			onClick,
		}: {
			value: string;
			onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
		},
		ref
	) => (
		<Button
			variant="basic"
			type="button"
			className="border border-grey-line-subtle"
			onClick={onClick}
			ref={ref}
		>
			{value}
		</Button>
	)
);

const dateUpdate = (operator: string, startDate: number) => {
	let newStart = startDate;
	if (operator === 'prev') {
		newStart += -7 * 24 * 60 * 60 * 1000;
	} else if (operator === 'next') {
		newStart += 7 * 24 * 60 * 60 * 1000;
	}
	return {
		newStart: new Date(newStart).getTime(),
		newEnd: new Date(newStart + 6 * 24 * 60 * 60 * 1000).getTime(),
	};
};
const renderDifferencePercentage = (diff) => (
	<div className={`flex items-center gap-2 ${diff >= 0 ? 'text-green-secondary' : 'text-red-primary'}`}>
		<p className='font-bold'>
			{parseFloat(diff.toFixed(1))}%
		</p>
		
		{ // eslint-disable-next-line no-nested-ternary
			diff >= 0 ? (diff === 0 ? <p>-</p> : <RiArrowUp/>)  : <RiArrowDown/> 
		}
	</div>
)

const Home = () => {
	const dashboardMethods = useForm();
	const {
		bookingCountThisMonth,
		bookingCountLastMonth,
		customerCountThisMonth,
		customerCountLastMonth,
		incomeThisMonth,
		incomeLastMonth,
	} = useSelector((state: RootState) => state.homeGraph.state);
	const bookingState = useSelector(
		(state: RootState) => state.booking.state.items
	);
	const venueState = useSelector(
		(state: RootState) => state.userInfo.state.venues
	);
	const venueDetailState = useSelector(
		(state: RootState) => state.venueDetail.venue
	);
	const { state: calendarItems } = useSelector(
		(state: RootState) => state.calendar
	);
	const [totalIncomeThisMonth, setTotalIncomeThisMonth] = useState(0)
	const [totalIncomeLastMonth, setTotalIncomeLastMonth] = useState(0)
	const [totalBookingThisMonth, setTotalBookingThisMonth] = useState(0)
	const [totalBookingLastMonth, setTotalBookingLastMonth] = useState(0)
	const [totalCustomerThisMonth, setTotalCustomerThisMonth] = useState(0)
	const [totalCustomerLastMonth, setTotalCustomerLastMonth] = useState(0)
	const [incomeDiff, setIncomeDiff] = useState(0)
	const [customerDiff, setCustomerDiff] = useState(0)
	const [bookingDiff, setBookingDiff] = useState(0)
	const [selectedDate, setSelectedDate] = useState(
		new Date().setHours(0, 0, 0, 0)
	);
	const [startDate, setStartDate] = useState(
		new Date().setDate(new Date().getDate() - ((new Date().getDay() + 6) % 7))
	);
	const [endDate, setEndDate] = useState(
		new Date().setDate(new Date(startDate).getDate() + 6)
	);
	const [bookingByCourt, setBookingByCourt] = useState([])
	const inputRef = useRef(null);
	const dispatch = useAppDispatch();

	useEffect(() => {
		(async () => {
			await dispatch(getUserVenues({}));
		})();
	}, []);
	useEffect(() => {
		const fetchCalendarItems = async () => {
			await dispatch(
				getCalendar({
					dateMeta: selectedDate,
					venueMeta: venueDetailState.id,
				})
			);
		};
		fetchCalendarItems();
	}, [venueDetailState, selectedDate]);
	useEffect(() => {
		setBookingByCourt([])
		const bookings = []
		calendarItems.forEach((calendarItem) => {
			const schedules = calendarItem.items?.map((item) => ({
				hour: item.start_time || 0,
				type: item.event_type || '',
				detail: item.description || item.customer?.name,
				eventId: item.event_id,
			}));
			bookings.push({
				code: calendarItem.court_code,
				id: calendarItem.court_id,
				schedules,
			});
		});
		setBookingByCourt(bookings)
	}, [calendarItems])
	useEffect(() => {
		const now = new Date()
        const tzOffsetHours = (now.getTimezoneOffset() / 60 ) * -1
        const currentMonthStartDateTime = new Date(now.getFullYear(), now.getMonth(), 1, (0 + tzOffsetHours), 0, 0).getTime();
        const currentMonthEndDateTime = now.getTime();

        const lastMonthStartDateTime = new Date(now.getFullYear(), now.getMonth()-1, 1, (0 + tzOffsetHours), 0, 0).getTime();
        const lastMonthEndDateTime = new Date(now.getFullYear(), now.getMonth(), 0, (23 + tzOffsetHours), 59, 59).getTime();
        
		
		const fetchBookingList = async () => {
			await dispatch(getBookingList({ venueMeta: venueDetailState.id, sizeMeta: 10 }));
		};
		const fetchThisMonthIncome = async () => {
			const startDateMeta = currentMonthStartDateTime.toString();
			const endDateMeta = currentMonthEndDateTime.toString();
			await dispatch(
				getIncomeGraph({
					startDateMeta,
					endDateMeta,
					venueMeta: venueDetailState.id,
					type: 'CURRENT_MONTH',
				})
			);
		};
		const fetchLastMonthIncome = async () => {
			const startDateMeta = lastMonthStartDateTime.toString();
			const endDateMeta = lastMonthEndDateTime.toString();
			await dispatch(
				getIncomeGraph({
					startDateMeta,
					endDateMeta,
					venueMeta: venueDetailState.id,
					type: 'LAST_MONTH',
				})
			);
		};

		const lastDays = 13; // n days - 1 (ex. 14 days ago = - 13 )
		const currentDate = now;
		const currentLastDaysStartTime = new Date(currentDate.setDate(now.getDate() - lastDays)).setHours(0 + tzOffsetHours);
		const prevLastDaysEndTime = new Date(currentDate).setHours(23 + tzOffsetHours);
		const prevLastDaysStartTime = new Date(currentDate.setDate(now.getDate() - lastDays)).setHours(0 + tzOffsetHours);

		const fetchCurrentTotalBookingReport = async () => {
			const startDateMeta = currentLastDaysStartTime.toString();
			const endDateMeta = new Date().getTime().toString();
			await dispatch(
				getCountGraph({
					startDateMeta,
					endDateMeta,
					venueMeta: venueDetailState.id,
					typeMeta: 'BOOKING',
					type: 'BOOKING_THIS_MONTH',
				})
			);
		};
		const fetchLastTotalBookingReport = async () => {
			const startDateMeta = prevLastDaysStartTime.toString();
			const endDateMeta = prevLastDaysEndTime.toString();
			await dispatch(
				getCountGraph({
					startDateMeta,
					endDateMeta,
					venueMeta: venueDetailState.id,
					typeMeta: 'BOOKING',
					type: 'BOOKING_LAST_MONTH',
				})
			);
		};
		const fetchCurrentTotalCustomerReport = async () => {
			const startDateMeta = currentLastDaysStartTime.toString();
			const endDateMeta = new Date().getTime().toString();
			await dispatch(
				getCountGraph({
					startDateMeta,
					endDateMeta,
					venueMeta: venueDetailState.id,
					typeMeta: 'CUSTOMER',
					type: 'CUSTOMER_THIS_MONTH',
				})
			);
		};
		const fetchLastTotalCustomerReport = async () => {
			const startDateMeta = prevLastDaysStartTime.toString();
			const endDateMeta = prevLastDaysEndTime.toString();
			await dispatch(
				getCountGraph({
					startDateMeta,
					endDateMeta,
					venueMeta: venueDetailState.id,
					typeMeta: 'CUSTOMER',
					type: 'CUSTOMER_LAST_MONTH',
				})
			);
		};
		fetchBookingList()
		fetchThisMonthIncome()
		fetchLastMonthIncome()
		fetchCurrentTotalBookingReport()
		fetchLastTotalBookingReport()
		fetchCurrentTotalCustomerReport()
		fetchLastTotalCustomerReport()
	}, [venueDetailState]);
	useEffect(() => {
		setTotalIncomeThisMonth(incomeThisMonth
			?.map((point) => point.income)
			?.reduce((total, income) => income + total, 0));
		setTotalIncomeLastMonth(incomeLastMonth
			?.map((point) => point.income)
			?.reduce((total, income) => income + total, 0));
		setTotalBookingThisMonth(bookingCountThisMonth
			?.map((point) => point.count)
			?.reduce((total, count) => count + total, 0));
		setTotalBookingLastMonth(bookingCountLastMonth
			?.map((point) => point.count)
			?.reduce((total, count) => count + total, 0));
		setTotalCustomerThisMonth(customerCountThisMonth
			?.map((point) => point.count)
			?.reduce((total, count) => count + total, 0));
		setTotalCustomerLastMonth(customerCountLastMonth
			?.map((point) => point.count)
			?.reduce((total, count) => count + total, 0));
		setIncomeDiff(totalIncomeThisMonth
			? (totalIncomeThisMonth - totalIncomeLastMonth) / totalIncomeLastMonth * 100
			: 0)
		setCustomerDiff(totalCustomerLastMonth
			? (totalCustomerThisMonth - totalCustomerLastMonth) / totalCustomerLastMonth * 100
			: 0)
		setBookingDiff(totalBookingLastMonth
			? (totalBookingThisMonth - totalBookingLastMonth) / totalBookingLastMonth * 100
			: 0)
	}, [bookingCountThisMonth,
		customerCountLastMonth,
		bookingCountLastMonth,
		incomeThisMonth,
		customerCountThisMonth,
		incomeLastMonth])

	const downloadReportUrl = ({ type, reportStartDate, reportEndDate, insightType }) => {
		dispatch(getReportCsvUrl({ type, reportStartDate, reportEndDate, insightType, venueId: venueDetailState.id })).then((res: { [key: string]: any }) => {
			if (!res.error) {
				const link = document.createElement('a');
				link.href = URL.createObjectURL(new Blob([res.payload.data], { type: 'text/csv' }));
				link.download = 'report.csv';
				link.dispatchEvent(new MouseEvent('click'));
				URL.revokeObjectURL(link.href);
			}
		})
	}
	const downloadCountUrl = ({ reportStartDate, reportEndDate, graphType }) => {
		dispatch(getCountCsvUrl({ reportStartDate, reportEndDate, graphType, venueId: venueDetailState.id })).then((res: { [key: string]: any }) => {
			if (!res.error) {
				const link = document.createElement('a');
				link.href = URL.createObjectURL(new Blob([res.payload.data], { type: 'text/csv' }));
				link.download = 'report.csv';
				link.dispatchEvent(new MouseEvent('click'));
				URL.revokeObjectURL(link.href);
			}
		})
	}

	return (
		<EmptyLayout>
			<div className="p-6 w-full flex flex-col gap-5">
				<FormProvider {...dashboardMethods}>
					<div className="flex gap-4 p-7 card">
						<div className="text-j2xl font-bold">Dashboard</div>
						<div className="separator" />
						<UserVenueSelect
							options={venueState.map((venue) => ({
								label: venue.name,
								value: venue.id,
							}))}
							placeholder="Select venue"
							onChange={(val: { label: string; value: string }) => {
								dispatch(getVenueDetail(val.value));
							}}
							isClearable
						/>
					</div>
					<div className="grid grid-cols-2 gap-6">
						<div className="flex flex-col items-center p-7 gap-4 bg-white">
							<div className="flex justify-between w-full items-center gap-4">
								<div className="flex flex-col gap-1">
									<p className="font-bold text-3xl sm:text-2xl">{currencyFormat(totalIncomeThisMonth)}</p>
									<p className="text-lg sm:text-base">Earning So Far This Month</p>
								</div>
								<div className="flex items-center gap-1">
									{renderDifferencePercentage(incomeDiff)}
								</div>
							</div>
							<div className="flex w-full items-center p-0 gap-4">
								{/* TODO: Not to hardcode label, format graphs, API for total price */}
								{incomeThisMonth?.length && (
									<ReportCard
										options={options}
										data={{
											labels: incomeThisMonth?.map(
												(dataPoint) => new Date(dataPoint.date).toLocaleDateString('en-GB', {
													month: 'long',
													day: 'numeric',
												})
											),
											datasets: [
												{
													fill: true,
													data: incomeThisMonth?.map(
														(dataPoint) => dataPoint.income
													),
													borderColor: 'rgb(53, 162, 235)',
													backgroundColor: 'rgba(53, 162, 235, 0.5)',
												},
											],
										}}
									/>
								)}
							</div>
							<div className="flex w-full justify-between items-center p-0 gap-6">
								<p>{new Date().toLocaleString('en-US', { month: 'long' })}</p>
								<button
									onClick={() => downloadReportUrl({
										type: 'INCOME_STATEMENT',
										reportStartDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).getTime(),
										reportEndDate: new Date().getTime(),
										insightType: 'INCOME_STATEMENT'
									})}
									className="cursor-pointer text-black-primary text-jsm font-medium flex gap-2 justify-center items-center py-2 px-6 rounded border border-stroke-subtle">
									<RiDownloadLine/>
									Download
								</button>
							</div>
						</div>
						<div className="flex flex-col items-center p-6 gap-4 bg-white">
							<div className="flex w-full justify-between items-center p-0 gap-4">
								<div className="flex flex-col p-0 gap-1">
									<p className="font-bold text-3xl sm:text-2xl">{currencyFormat(totalIncomeLastMonth)}</p>
									<p className="text-lg">Earning Last Month</p>
								</div>
								<div className="flex items-center gap-1">
									{renderDifferencePercentage(incomeDiff * -1)}
								</div>
							</div>
							<div className="flex w-full items-center p-0 gap-4">
								{incomeLastMonth?.length && (
									<ReportCard
										options={options}
										data={{
											labels: incomeLastMonth?.map(
												(dataPoint) => new Date(dataPoint.date).toLocaleDateString('en-GB', {
													month: 'long',
													day: 'numeric',
												})
											),
											datasets: [
												{
													fill: true,
													data: incomeLastMonth?.map(
														(dataPoint) => dataPoint.income
													),
													borderColor: 'rgb(53, 162, 235)',
													backgroundColor: 'rgba(53, 162, 235, 0.5)',
												},
											],
										}}
									/>
								)}
							</div>
							<div className="flex w-full justify-between items-center p-0 gap-6">
								<p>
									{new Date(
										new Date().setMonth(new Date().getMonth() - 1)
									).toLocaleString('en-US', { month: 'long' })}
								</p>
								<button
									onClick={() => downloadReportUrl({
										type: 'INCOME_STATEMENT',
										reportStartDate: new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1).getTime(),
										reportEndDate: new Date(new Date().getFullYear(), new Date().getMonth(), 0).getTime(),
										insightType: 'INCOME_STATEMENT'
									})}
									className="cursor-pointer text-black-primary text-jsm font-medium flex gap-2 justify-center items-center py-2 px-6 rounded border border-stroke-subtle">
									<RiDownloadLine/>
									Download
								</button>
							</div>
						</div>
						{/*<ReportCard title='Earning Last Month' totalPrice={20000000} />*/}
					</div>
					<div className="grid grid-cols-4 gap-6">
						<div className="col-span-3 flex flex-col items-center p-7 gap-4 bg-white">
							<div className="flex justify-between w-full gap-2">
								<Button
									variant="basic"
									type="button"
									className="border border-grey-line-subtle"
									onClick={() => {
										const newDates = dateUpdate('', new Date().getTime());
										setStartDate(newDates.newStart);
										setEndDate(newDates.newEnd);
									}}
								>
									Hari Ini
								</Button>
								<div className="flex gap-2">
									<Button
										variant="basic"
										type="button"
										className="border border-grey-line-subtle"
										onClick={() => {
											const newDates = dateUpdate('prev', startDate);
											setStartDate(newDates.newStart);
											setEndDate(newDates.newEnd);
										}}
									>
										<RiArrowLeft />
									</Button>
									<DatePicker
										selectsRange
										startDate={startDate}
										endDate={endDate}
										onChange={(date) => {
											const { newStart, newEnd } = dateUpdate(
												'',
												date[0].getTime()
											);
											setStartDate(newStart);
											setEndDate(newEnd);
										}}
										customInput={<ShowCalendar ref={inputRef} />}
									/>
									<Button
										variant="basic"
										type="button"
										className="border border-grey-line-subtle"
										onClick={() => {
											const newDates = dateUpdate('next', startDate);
											setStartDate(newDates.newStart);
											setEndDate(newDates.newEnd);
										}}
									>
										<RiArrowRight />
									</Button>
								</div>
							</div>
							<Calendar
								startDate={startDate}
								selectedDate={selectedDate}
								setSelectedDate={setSelectedDate}
								hours={OPENING_HOURS}
								courts={venueDetailState?.courts || []}
								bookingByCourt={bookingByCourt}
								displayOnly
							/>
						</div>
						<div>
							<div className="flex flex-col p-0 bg-white">
								<div className="flex items-center p-4">
									<p className="font-bold text-lg">Latest Booking</p>
								</div>
								<div className="w-full">
									<Table
										headers={HEADERS}
										body={constructTableData(bookingState)}
										sort={SORT}
										meta={META}
										renderMeta={false}
									/>
								</div>
							</div>
						</div>
						{/*<ReportCard title='Earning Last Month' totalPrice={20000000} />*/}
					</div>
					<div className="grid grid-cols-2 gap-6">
						<div className="flex flex-col w-full items-center p-6 gap-4 bg-white">
							<div className="flex justify-between w-full items-center gap-4">
								<div className="flex flex-col gap-1">
									<p className="font-bold text-3xl">{totalBookingThisMonth}</p>
									<p className="text-lg">Total Booking</p>
								</div>
								<div className="flex items-center gap-1">
									{renderDifferencePercentage(bookingDiff)}
								</div>
							</div>
							<div className="flex items-center w-full p-0 gap-4">
								{bookingCountThisMonth?.length && (
									<ReportCard
										options={options}
										data={{
											labels: bookingCountThisMonth?.map(
												(dataPoint) => new Date(dataPoint.date).toLocaleDateString('en-GB', {
													month: 'long',
													day: 'numeric',
												})
											),
											datasets: [
												{
													fill: true,
													data: bookingCountThisMonth?.map(
														(dataPoint) => dataPoint.count
													),
													borderColor: 'rgb(53, 162, 235)',
													backgroundColor: 'rgba(53, 162, 235, 0.5)',
												},
											],
										}}
									/>
								)}
							</div>
							<div className="w-full border-b border-grey-line-subtle" />
							<div className="flex justify-between items-center w-full gap-6">
								<div className="flex items-start gap-3">
									<p className="font-grey-dark">Last 14 Days</p>
								</div>
								<button
									onClick={() => downloadCountUrl({
										reportStartDate: new Date(new Date().setDate(new Date().getDate() - 14)).getTime(),
										reportEndDate: new Date().getTime(),
										graphType: 'BOOKING'
									})}
									className="cursor-pointer text-black-primary text-jsm font-medium flex gap-2 justify-center items-center py-2 px-6 rounded border border-stroke-subtle">
									<RiDownloadLine/>
									Download
								</button>
							</div>
						</div>
						<div className="flex flex-col w-full items-center p-6 gap-4 bg-white">
							<div className="flex justify-between w-full items-center gap-4">
								<div className="flex flex-col gap-1">
									<p className="font-bold text-3xl">{totalCustomerThisMonth}</p>
									<p className="text-lg">Total Customer</p>
								</div>
								<div className="flex items-center gap-1">
									{renderDifferencePercentage(customerDiff)}
								</div>
							</div>
							<div className="flex items-center w-full p-0 gap-4">
								{customerCountThisMonth?.length && (
									<ReportCard
										options={options}
										data={{
											labels: customerCountThisMonth?.map(
												(dataPoint) => new Date(dataPoint.date).toLocaleDateString('en-GB', {
													month: 'long',
													day: 'numeric',
												})
											),
											datasets: [
												{
													fill: true,
													data: customerCountThisMonth?.map(
														(dataPoint) => dataPoint.count
													),
													borderColor: 'rgb(53, 162, 235)',
													backgroundColor: 'rgba(53, 162, 235, 0.5)',
												},
											],
										}}
									/>
								)}
							</div>
							<div className="w-full border-b border-grey-line-subtle" />
							<div className="flex justify-between items-center w-full gap-6">
								<div className="flex items-start gap-3">
									<p className="font-grey-dark">Last 14 Days</p>
								</div>
								<button
									onClick={() => downloadCountUrl({
										reportStartDate: new Date(new Date().setDate(new Date().getDate() - 14)).getTime(),
										reportEndDate: new Date().getTime(),
										graphType: 'CUSTOMER'
									})}
									className="cursor-pointer text-black-primary text-jsm font-medium flex gap-2 justify-center items-center py-2 px-6 rounded border border-stroke-subtle">
									<RiDownloadLine/>
									Download
								</button>
							</div>
						</div>
					</div>
				</FormProvider>
			</div>
		</EmptyLayout>
	);
};

export default Home;
